import * as Yup from 'yup';
import { createContext, useContext } from 'react';

const ValidationSchemaContext = createContext<Yup.AnySchema | null>(null);

/**
 * A component that provides a context to access a validation schema.
 * It wraps the children element with ValidationSchemaContext.Provider and passes the validationSchema as its value.
 * This component should be used to wrap components that require access to a validation schema,
 * such as Field components.
 *
 * @param validationSchema - The validation schema to provide in the context.
 * @param children - The children elements to render within the ValidationSchemaContext.Provider.
 */
export function ValidationSchemaProvider({
  validationSchema,
  children,
}: {
  children: React.ReactNode;
  validationSchema: Yup.AnySchema;
}) {
  return (
    <ValidationSchemaContext.Provider value={validationSchema}>
      {children}
    </ValidationSchemaContext.Provider>
  );
}

/**
 * Returns the validation schema from the context.
 * It can be used to access the validation schema within components that are wrapped with ValidationSchemaProvider.
 * @returns The validation schema from the context.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useValidationSchema() {
  return useContext(ValidationSchemaContext);
}
